import React, { useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { usePopup } from '../../hooks/usePopup';
import { getDisplayDelay, markPopupAsShown } from '../../utils/popupHelpers';
import { KTSVG, toAbsoluteUrl } from '../../_metronic/helpers';
import PopupRenderer from './PopupRenderer';
import './PopupContainer.scss';

const PopupContainer: React.FC = () => {
  const { activePopup, closePopup } = usePopup();
  const [isVisible, setIsVisible] = useState(false);
  const exitIntentTriggered = useRef(false);
  const scrollTriggered = useRef(false);

  useEffect(() => {
    if (!activePopup) {
      setIsVisible(false);
      exitIntentTriggered.current = false;
      scrollTriggered.current = false;
      return;
    }

    const delay = getDisplayDelay(activePopup);

    if (delay === -1) {
      // onExit - Mouse ekranın üstüne çıkınca
      const handleMouseLeave = (e: MouseEvent) => {
        if (!exitIntentTriggered.current && e.clientY <= 0) {
          exitIntentTriggered.current = true;
          setIsVisible(true);
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave);
      return () => document.removeEventListener('mouseleave', handleMouseLeave);
    } else if (delay === -2) {
      // onScroll - 100px scroll yapınca
      const handleScroll = () => {
        if (scrollTriggered.current) return;

        if (window.scrollY > 100) {
          scrollTriggered.current = true;
          setIsVisible(true);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // onLoad - Sayfa yüklenince
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [activePopup]);

  // Popup gösterildiğinde işaretle
  useEffect(() => {
    if (isVisible && activePopup) {
      markPopupAsShown(activePopup);
    }
  }, [isVisible, activePopup]);

  // Escape key ile kapat
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        closePopup();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, closePopup]);

  if (!activePopup) return null;

  const handleClose = () => {
    closePopup();
  };

  return (
    <Modal
      className="modal fade"
      id="kt_modal_popup_system"
      data-backdrop="static"
      tabIndex={-1}
      role="dialog"
      show={isVisible}
      aria-hidden="true"
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="popup-modal-880"
    >
      <div className="modal-content">
        <div className="modal-header border-0 pb-0 position-relative px-12 pt-6">
          <img 
            alt="Logo" 
            className="popup-modal-logo" 
            src={toAbsoluteUrl('/media/logos/logo-idefix.svg')} 
          />
          
          <div 
            className="btn btn-icon btn-sm btn-active-light-primary popup-modal-close" 
            onClick={handleClose}
          >
            <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-2x" />
          </div>
        </div>

        <div className="modal-body text-center pt-7 px-12">
          {activePopup.blocks && activePopup.blocks.length > 0 ? (
            <div className="popup-blocks">
              <PopupRenderer blocks={activePopup.blocks} />
            </div>
          ) : (
            <p className="text-gray-600">No content available</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PopupContainer;
